import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, } from "@/components/ui/input-group";
import { X } from "lucide-react";
import { api } from "../../services/search";
import { ContentType } from "../../types/global";
import GenericSectionSkeleton from "../homepage/GenericSectionSkeleton";
import PlaceholderLayer from "../homepage/PlaceholderLayer";
import SearchLayer from "./SearchLayer";
import { useDebounceValue } from "usehooks-ts";
// ! Al momento la search ha questo problema per cui le pagine dopo la prima avranno gli elementi che si caricano in comune (es. se io scrollo fino alla fine dello swiper verticale mi si caricano altri risultati ma possibilmente nell'altro swiper poichè non esiste ricerca per l'orientamento dei video al momento)
export default function Search({ setIsOpen, className = "" }) {
    const { t } = useTranslation();
    const [mounted, setMounted] = React.useState(false);
    const [input, setInput] = React.useState("");
    const [isInputFocus, setIsInputFocus] = React.useState(true);
    const [debouncedInput] = useDebounceValue(input, 2000);
    const inputRef = React.useRef(null);
    const { data, hasNextPage, isFetchingNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
        queryKey: ["search-result", input],
        queryFn: ({ pageParam }) => {
            return api.search.content({
                current_page: pageParam,
                value: debouncedInput,
                content_type: [
                    ContentType.Video,
                    ContentType.Livestream,
                    ContentType.Fastchannels,
                    ContentType.Folder,
                    ContentType.Reels,
                    ContentType.Shorts,
                ],
            });
        },
        // placeholderData: (previousData) => previousData,
        enabled: input.trim() !== "" && input === debouncedInput,
        initialPageParam: 0,
        getNextPageParam: (lastPage, _, lastPageParam) => lastPage.content_list && lastPage.content_list?.length > 0
            ? lastPageParam + 1
            : undefined,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchInterval: false,
        refetchOnMount: true,
        staleTime: 0,
    });
    React.useEffect(() => {
        const id = requestAnimationFrame(() => setMounted(true));
        return () => cancelAnimationFrame(id);
    }, []);
    const handleOnInput = (event) => {
        setInput(event.currentTarget.value);
    };
    const handleClear = () => {
        setInput("");
        inputRef.current?.focus();
    };
    const slides = {
        current_page: 0,
        total_count_page: 0,
        content_list: data?.pages.flatMap((page) => page.content_list) ?? [],
    };
    const contentfulSearch = slides.content_list.length > 0;
    const handleReachSectionEnd = () => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    };
    const showSkeleton = input && (!contentfulSearch || isFetching);
    React.useEffect(() => {
        if (!contentfulSearch && !showSkeleton && !input && !isInputFocus) {
            setIsOpen(false);
        }
    }, [contentfulSearch, showSkeleton, isInputFocus]);
    return (_jsx(_Fragment, { children: _jsxs("div", { className: `${className ?? ""} origin-top transition-all duration-200 ease-out ${mounted ? "scale-100 opacity-100" : "scale-95 opacity-0"}`, children: [contentfulSearch ? (_jsx(SearchLayer, { data: slides, onReachEnd: handleReachSectionEnd, onClose: () => {
                        setInput("");
                        setIsOpen(false);
                    } })) : (showSkeleton && (_jsxs(PlaceholderLayer, { children: [_jsx(GenericSectionSkeleton, { enableTitle: false, type: "catalogue" }), _jsx(GenericSectionSkeleton, { enableTitle: false, type: "generic-landscape" }), _jsx(GenericSectionSkeleton, { enableTitle: false, type: "generic-portrait" })] }))), _jsxs(InputGroup, { className: "bg-background", children: [_jsx(InputGroupInput, { ref: inputRef, autoFocus: true, placeholder: t("search-trailing-dots"), onInput: handleOnInput, value: input, className: "text-generic", onFocus: () => setIsInputFocus(true), onBlur: () => setIsInputFocus(false) }), _jsx(InputGroupAddon, { align: "inline-end", className: "gap-0", children: input.length > 0 && (_jsx(InputGroupButton, { onClick: handleClear, className: "cursor-pointer", children: _jsx(X, { className: "h-5 w-5 shrink-0" }) })) })] })] }) }));
}
